import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type {
  League,
  Division,
  Season,
  Category,
  CreateLeagueRequest,
  UpdateLeagueRequest,
  CreateSeasonRequest,
  UpdateSeasonRequest,
  CreateDivisionRequest,
  UpdateDivisionRequest,
  PointsConfig,
  LeagueAdministrator,
  SeasonSubscribersResponse,
} from '@sulis-liiga/common';
import leagueService, { type LeagueSummary } from '../../services/leagueService';
import userService from '../../services/userService';

// Define the state interface
interface LeagueState {
  leagues: League[];
  currentLeague: League | null;
  divisions: Division[];
  currentDivision: Division | null;
  currentSeason: Season | null;
  /** All seasons for the currently viewed league (upcoming + active + completed). */
  seasons: Season[];
  /** Subscribed players/teams for a season, grouped by category (admin view). */
  seasonSubscribers: SeasonSubscribersResponse | null;
  summary: LeagueSummary | null;
  pointsConfig: PointsConfig | null;
  categories: Category[];
  loading: boolean;
  error: string | null;
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
  meta: {
    page?: number;
    limit?: number;
    total?: number;
    pages?: number;
  };
  // League administrator management
  administrators: LeagueAdministrator[];
  adminLoading: boolean;
  adminError: string | null;
  adminSuccess: boolean;
}

// Initial state
const initialState: LeagueState = {
  leagues: [],
  currentLeague: null,
  divisions: [],
  currentDivision: null,
  currentSeason: null,
  seasons: [],
  seasonSubscribers: null,
  summary: null,
  pointsConfig: null,
  categories: [],
  loading: false,
  error: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
  meta: {},
  // League administrator management
  administrators: [],
  adminLoading: false,
  adminError: null,
  adminSuccess: false,
};

// ─── Season List Thunks ──────────────────────────────────────────────────────

export const fetchSeasons = createAsyncThunk(
  'league/fetchSeasons',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      return await leagueService.getSeasons(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch seasons. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchSeasonSubscribers = createAsyncThunk(
  'league/fetchSeasonSubscribers',
  async ({ leagueId, seasonId }: { leagueId: string; seasonId: string }, { rejectWithValue }) => {
    try {
      return await leagueService.getSeasonSubscribers(leagueId, seasonId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch season subscribers. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

// ─── League Administrator Thunks ─────────────────────────────────────────────

export const fetchLeagueAdministrators = createAsyncThunk(
  'league/fetchLeagueAdministrators',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      return await leagueService.getLeagueAdministrators(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch league administrators. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const assignLeagueAdministrator = createAsyncThunk(
  'league/assignLeagueAdministrator',
  async ({ leagueId, email }: { leagueId: string; email: string }, { rejectWithValue }) => {
    try {
      // Resolve email → userId using the user search endpoint
      const user = await userService.findUserByEmail(email);
      if (!user) {
        return rejectWithValue('No user found with that email address.');
      }
      await leagueService.assignLeagueAdministrator(leagueId, { userId: user.id });
      // Return the updated admin list so the slice can refresh state
      return await leagueService.getLeagueAdministrators(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to assign administrator. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const revokeLeagueAdministrator = createAsyncThunk(
  'league/revokeLeagueAdministrator',
  async ({ leagueId, userId }: { leagueId: string; userId: string }, { rejectWithValue }) => {
    try {
      await leagueService.revokeLeagueAdministrator(leagueId, userId);
      // Return the updated admin list
      return await leagueService.getLeagueAdministrators(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to revoke administrator. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

// ─── League Thunks ────────────────────────────────────────────────────────────

export const fetchLeagues = createAsyncThunk(
  'league/fetchLeagues',
  async (params: { page?: number; limit?: number } | undefined, { rejectWithValue }) => {
    try {
      const result = await leagueService.getLeagues(params);
      return { data: result.data, meta: result.meta };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch leagues. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchLeague = createAsyncThunk(
  'league/fetchLeague',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      return await leagueService.getLeague(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch league details. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const createLeague = createAsyncThunk(
  'league/createLeague',
  async (leagueData: CreateLeagueRequest, { rejectWithValue }) => {
    try {
      return await leagueService.createLeague(leagueData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create league. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateLeague = createAsyncThunk(
  'league/updateLeague',
  async (
    { leagueId, leagueData }: { leagueId: string; leagueData: UpdateLeagueRequest },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.updateLeague(leagueId, leagueData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update league. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteLeague = createAsyncThunk(
  'league/deleteLeague',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      await leagueService.deleteLeague(leagueId);
      return leagueId;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete league. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const createSeason = createAsyncThunk(
  'league/createSeason',
  async (
    { leagueId, seasonData }: { leagueId: string; seasonData: CreateSeasonRequest },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.createSeason(leagueId, seasonData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create season. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateSeason = createAsyncThunk(
  'league/updateSeason',
  async (
    {
      leagueId,
      seasonId,
      seasonData,
    }: { leagueId: string; seasonId: string; seasonData: UpdateSeasonRequest },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.updateSeason(leagueId, seasonId, seasonData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update season. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchPointsConfig = createAsyncThunk(
  'league/fetchPointsConfig',
  async ({ leagueId, seasonId }: { leagueId: string; seasonId: string }, { rejectWithValue }) => {
    try {
      return await leagueService.getPointsConfig(leagueId, seasonId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch points configuration. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const updatePointsConfig = createAsyncThunk(
  'league/updatePointsConfig',
  async (
    {
      leagueId,
      seasonId,
      pointsConfig,
    }: { leagueId: string; seasonId: string; pointsConfig: PointsConfig },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.updatePointsConfig(leagueId, seasonId, pointsConfig);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update points configuration. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const createDivision = createAsyncThunk(
  'league/createDivision',
  async (
    {
      leagueId,
      seasonId,
      divisionData,
    }: { leagueId: string; seasonId: string; divisionData: CreateDivisionRequest },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.createDivision(leagueId, seasonId, divisionData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create division. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateDivision = createAsyncThunk(
  'league/updateDivision',
  async (
    {
      leagueId,
      seasonId,
      divisionId,
      divisionData,
    }: {
      leagueId: string;
      seasonId: string;
      divisionId: string;
      divisionData: UpdateDivisionRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.updateDivision(leagueId, seasonId, divisionId, divisionData);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update division. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteDivision = createAsyncThunk(
  'league/deleteDivision',
  async (
    { leagueId, seasonId, divisionId }: { leagueId: string; seasonId: string; divisionId: string },
    { rejectWithValue },
  ) => {
    try {
      await leagueService.deleteDivision(leagueId, seasonId, divisionId);
      return divisionId;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete division. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchCategories = createAsyncThunk(
  'league/fetchCategories',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      return await leagueService.getCategories(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch categories. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchDivisionsByCategory = createAsyncThunk(
  'league/fetchDivisionsByCategory',
  async (
    { leagueId, categoryId }: { leagueId: string; categoryId: Category },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.getDivisionsByCategory(leagueId, categoryId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch divisions. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchLeagueSeason = createAsyncThunk(
  'league/fetchLeagueSeason',
  async ({ leagueId, seasonId }: { leagueId: string; seasonId: string }, { rejectWithValue }) => {
    try {
      return await leagueService.getLeagueSeason(leagueId, seasonId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch season. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchDivisionsForSeason = createAsyncThunk(
  'league/fetchDivisionsForSeason',
  async (
    { leagueId, seasonId, category }: { leagueId: string; seasonId: string; category?: Category },
    { rejectWithValue },
  ) => {
    try {
      return await leagueService.getDivisionsForSeason(leagueId, seasonId, category);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch divisions. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchLeagueSummary = createAsyncThunk(
  'league/fetchLeagueSummary',
  async (leagueId: string, { rejectWithValue }) => {
    try {
      return await leagueService.getLeagueSummary(leagueId);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch league summary.';
      return rejectWithValue(errorMessage);
    }
  },
);

// Create the league slice
const leagueSlice = createSlice({
  name: 'league',
  initialState,
  reducers: {
    clearLeagueError: state => {
      state.error = null;
    },
    clearLeagueSuccess: state => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    clearAdminError: state => {
      state.adminError = null;
    },
    clearAdminSuccess: state => {
      state.adminSuccess = false;
    },
    setCurrentLeague: (state, action: PayloadAction<League | null>) => {
      state.currentLeague = action.payload;
    },
    setCurrentDivision: (state, action: PayloadAction<Division | null>) => {
      state.currentDivision = action.payload;
    },
    setCurrentSeason: (state, action: PayloadAction<Season | null>) => {
      state.currentSeason = action.payload;
    },
  },
  extraReducers: builder => {
    // fetchLeagues cases
    builder.addCase(fetchLeagues.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeagues.fulfilled, (state, action) => {
      state.loading = false;
      state.leagues = action.payload.data;
      state.meta = action.payload.meta || {};
      state.error = null;
    });
    builder.addCase(fetchLeagues.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchLeague cases
    builder.addCase(fetchLeague.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeague.fulfilled, (state, action) => {
      state.loading = false;
      state.currentLeague = action.payload;
      state.error = null;
    });
    builder.addCase(fetchLeague.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // createLeague cases
    builder.addCase(createLeague.pending, state => {
      state.loading = true;
      state.error = null;
      state.createSuccess = false;
    });
    builder.addCase(createLeague.fulfilled, (state, action) => {
      state.loading = false;
      state.leagues.push(action.payload);
      state.createSuccess = true;
      state.error = null;
    });
    builder.addCase(createLeague.rejected, (state, action) => {
      state.loading = false;
      state.createSuccess = false;
      state.error = action.payload as string;
    });

    // updateLeague cases
    builder.addCase(updateLeague.pending, state => {
      state.loading = true;
      state.error = null;
      state.updateSuccess = false;
    });
    builder.addCase(updateLeague.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.leagues.findIndex(l => l.id === action.payload.id);
      if (index !== -1) {
        state.leagues[index] = action.payload;
      }
      if (state.currentLeague?.id === action.payload.id) {
        state.currentLeague = action.payload;
      }
      state.updateSuccess = true;
      state.error = null;
    });
    builder.addCase(updateLeague.rejected, (state, action) => {
      state.loading = false;
      state.updateSuccess = false;
      state.error = action.payload as string;
    });

    // deleteLeague cases
    builder.addCase(deleteLeague.pending, state => {
      state.loading = true;
      state.error = null;
      state.deleteSuccess = false;
    });
    builder.addCase(deleteLeague.fulfilled, (state, action) => {
      state.loading = false;
      state.leagues = state.leagues.filter(l => l.id !== action.payload);
      if (state.currentLeague?.id === action.payload) {
        state.currentLeague = null;
      }
      state.deleteSuccess = true;
      state.error = null;
    });
    builder.addCase(deleteLeague.rejected, (state, action) => {
      state.loading = false;
      state.deleteSuccess = false;
      state.error = action.payload as string;
    });

    // createSeason cases
    builder.addCase(createSeason.pending, state => {
      state.loading = true;
      state.error = null;
      state.createSuccess = false;
    });
    builder.addCase(createSeason.fulfilled, (state, action) => {
      state.loading = false;
      state.currentSeason = action.payload;
      state.createSuccess = true;
      state.error = null;
    });
    builder.addCase(createSeason.rejected, (state, action) => {
      state.loading = false;
      state.createSuccess = false;
      state.error = action.payload as string;
    });

    // updateSeason cases
    builder.addCase(updateSeason.pending, state => {
      state.loading = true;
      state.error = null;
      state.updateSuccess = false;
    });
    builder.addCase(updateSeason.fulfilled, (state, action) => {
      state.loading = false;
      state.currentSeason = action.payload;
      state.updateSuccess = true;
      state.error = null;
    });
    builder.addCase(updateSeason.rejected, (state, action) => {
      state.loading = false;
      state.updateSuccess = false;
      state.error = action.payload as string;
    });

    // fetchPointsConfig cases
    builder.addCase(fetchPointsConfig.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPointsConfig.fulfilled, (state, action) => {
      state.loading = false;
      state.pointsConfig = action.payload;
      state.error = null;
    });
    builder.addCase(fetchPointsConfig.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // updatePointsConfig cases
    builder.addCase(updatePointsConfig.pending, state => {
      state.loading = true;
      state.error = null;
      state.updateSuccess = false;
    });
    builder.addCase(updatePointsConfig.fulfilled, (state, action) => {
      state.loading = false;
      state.pointsConfig = action.payload;
      state.updateSuccess = true;
      state.error = null;
    });
    builder.addCase(updatePointsConfig.rejected, (state, action) => {
      state.loading = false;
      state.updateSuccess = false;
      state.error = action.payload as string;
    });

    // createDivision cases
    builder.addCase(createDivision.pending, state => {
      state.loading = true;
      state.error = null;
      state.createSuccess = false;
    });
    builder.addCase(createDivision.fulfilled, (state, action) => {
      state.loading = false;
      // createDivision now returns Division[] (one per tier in the ladder)
      state.divisions.push(...action.payload);
      state.createSuccess = true;
      state.error = null;
    });
    builder.addCase(createDivision.rejected, (state, action) => {
      state.loading = false;
      state.createSuccess = false;
      state.error = action.payload as string;
    });

    // updateDivision cases
    builder.addCase(updateDivision.pending, state => {
      state.loading = true;
      state.error = null;
      state.updateSuccess = false;
    });
    builder.addCase(updateDivision.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.divisions.findIndex(d => d.id === action.payload.id);
      if (index !== -1) {
        state.divisions[index] = action.payload;
      }
      if (state.currentDivision?.id === action.payload.id) {
        state.currentDivision = action.payload;
      }
      state.updateSuccess = true;
      state.error = null;
    });
    builder.addCase(updateDivision.rejected, (state, action) => {
      state.loading = false;
      state.updateSuccess = false;
      state.error = action.payload as string;
    });

    // deleteDivision cases
    builder.addCase(deleteDivision.pending, state => {
      state.loading = true;
      state.error = null;
      state.deleteSuccess = false;
    });
    builder.addCase(deleteDivision.fulfilled, (state, action) => {
      state.loading = false;
      state.divisions = state.divisions.filter(d => d.id !== action.payload);
      if (state.currentDivision?.id === action.payload) {
        state.currentDivision = null;
      }
      state.deleteSuccess = true;
      state.error = null;
    });
    builder.addCase(deleteDivision.rejected, (state, action) => {
      state.loading = false;
      state.deleteSuccess = false;
      state.error = action.payload as string;
    });

    // fetchCategories cases
    builder.addCase(fetchCategories.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCategories.fulfilled, (state, action) => {
      state.loading = false;
      state.categories = action.payload;
      state.error = null;
    });
    builder.addCase(fetchCategories.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchDivisionsByCategory cases
    builder.addCase(fetchDivisionsByCategory.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDivisionsByCategory.fulfilled, (state, action) => {
      state.loading = false;
      state.divisions = action.payload;
      state.error = null;
    });
    builder.addCase(fetchDivisionsByCategory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchLeagueSeason cases
    builder.addCase(fetchLeagueSeason.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeagueSeason.fulfilled, (state, action) => {
      state.loading = false;
      state.currentSeason = action.payload;
      state.error = null;
    });
    builder.addCase(fetchLeagueSeason.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchDivisionsForSeason cases
    builder.addCase(fetchDivisionsForSeason.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDivisionsForSeason.fulfilled, (state, action) => {
      state.loading = false;
      state.divisions = action.payload;
      state.error = null;
    });
    builder.addCase(fetchDivisionsForSeason.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // League summary (Home page snapshot) — kept independent of `loading`/`error`
    // so a failed/absent summary never blocks the rest of the page.
    builder.addCase(fetchLeagueSummary.fulfilled, (state, action) => {
      state.summary = action.payload;
    });
    builder.addCase(fetchLeagueSummary.rejected, state => {
      state.summary = null;
    });

    // fetchSeasons cases — secondary fetch, do NOT touch shared loading/error
    builder.addCase(fetchSeasons.fulfilled, (state, action) => {
      state.seasons = action.payload;
    });

    // fetchSeasonSubscribers cases — secondary fetch, do NOT touch shared loading/error
    builder.addCase(fetchSeasonSubscribers.fulfilled, (state, action) => {
      state.seasonSubscribers = action.payload;
    });

    // fetchLeagueAdministrators cases
    builder.addCase(fetchLeagueAdministrators.pending, state => {
      state.adminLoading = true;
      state.adminError = null;
    });
    builder.addCase(fetchLeagueAdministrators.fulfilled, (state, action) => {
      state.adminLoading = false;
      state.administrators = action.payload;
      state.adminError = null;
    });
    builder.addCase(fetchLeagueAdministrators.rejected, (state, action) => {
      state.adminLoading = false;
      state.adminError = action.payload as string;
    });

    // assignLeagueAdministrator cases
    builder.addCase(assignLeagueAdministrator.pending, state => {
      state.adminLoading = true;
      state.adminError = null;
      state.adminSuccess = false;
    });
    builder.addCase(assignLeagueAdministrator.fulfilled, (state, action) => {
      state.adminLoading = false;
      state.administrators = action.payload;
      state.adminError = null;
      state.adminSuccess = true;
    });
    builder.addCase(assignLeagueAdministrator.rejected, (state, action) => {
      state.adminLoading = false;
      state.adminError = action.payload as string;
      state.adminSuccess = false;
    });

    // revokeLeagueAdministrator cases
    builder.addCase(revokeLeagueAdministrator.pending, state => {
      state.adminLoading = true;
      state.adminError = null;
      state.adminSuccess = false;
    });
    builder.addCase(revokeLeagueAdministrator.fulfilled, (state, action) => {
      state.adminLoading = false;
      state.administrators = action.payload;
      state.adminError = null;
      state.adminSuccess = true;
    });
    builder.addCase(revokeLeagueAdministrator.rejected, (state, action) => {
      state.adminLoading = false;
      state.adminError = action.payload as string;
      state.adminSuccess = false;
    });
  },
});

export const {
  clearLeagueError,
  clearLeagueSuccess,
  clearAdminError,
  clearAdminSuccess,
  setCurrentLeague,
  setCurrentDivision,
  setCurrentSeason,
} = leagueSlice.actions;

export default leagueSlice.reducer;
